import { LoginResult, performExternalLogin, performLogin } from "@/lib/auth/login";
import { APPLE_HASH_TOKEN, AuthMethods, FACEBOOK_HASH } from "@/lib/consts/auth";
import { JWT_SESSION_STORAGE_KEY, LOGIN_QUERY_PARAMS, URL_PARAMS } from "@/lib/consts/params";
import { BootAuthResponse } from "@/lib/types";
import { navigateToShopHard, redirectBasedOnLoginResponse } from "@/lib/utils/navigation";
import { ROUTES } from "@/lib/consts/routes";
import {
    buildShopUrlForLoggedInCampaignCheckout,
    getCampaignTokenFromUrl,
    getCheckoutTokenFromUrl,
    getHashParam,
} from "@/lib/utils/url";
import sessionService from "@/lib/services/session";
import { sendPreLoginPublisherWebhookEvents, sendLoginCanceledWebhookEvents } from "@/lib/utils/login";
import { isWebviewBrowser } from "@/lib/utils/webview";
import analytics from "@/lib/services/analytics";
import { AnalyticsEventName } from "@/lib/consts/analytics";
import { getDeviceOrientation } from "@/lib/utils/device";
import { useRouter } from "next/navigation";
import { getUtmParams, getUtms } from "@/lib/utils/utm";
import { LoggerEventNames } from "@/lib/consts/logger";
import coralogixRumService from "@/lib/services/coralogixRum";
import { RumEventLevel } from "@/lib/analytics/pendingRumEvents";
import { webStorageUtil } from "@/lib/storage/webStorageUtil";
import { ELocalStorageKeys, ESessionStorageKeys } from "@/lib/storage/enums";
import { FeatureFlags } from "@/lib/consts/featureFlags";
import { setSessionCookieWithConfirmation } from "@/lib/auth/sessionRestore";
import { releaseLoginLoaderHandoff } from "@/components/StoreLoadingCover/loginLoaderBackgroundSession";
import { setLoginLoaderBgMode } from "@/components/StoreLoadingCover/StoreLoadingCover.utils";

export interface LoginActionResult {
    success: boolean;
    error?: string;
    redirected?: boolean;
}

/**
 * Steps that can fail without being an auth method, so they stay out of
 * AuthMethods (which is part of the publisher event contract).
 */
enum LoginStage {
    SESSION_DATA = 'session-data',
    PRE_LOGIN = 'pre-login',
}

export const useLoginActions = (authBoot: BootAuthResponse, queryParams: URLSearchParams) => {
    const router = useRouter();
    const localAddress = queryParams.get(LOGIN_QUERY_PARAMS.LOCAL_ADDRESS) || webStorageUtil.get(ELocalStorageKeys.LOCAL_ADDRESS) || '';

    return {
        handlePortalLoginRedirect: async (redirectLoginToken: string): Promise<LoginActionResult> => {
            try {
                const { result, failedLoginUrl } = await performLogin({
                    authMethod: AuthMethods.PORTAL_REDIRECT,
                    localAddress,
                    userToken: redirectLoginToken
                }, authBoot);

                if (result === LoginResult.SUCCESS) {
                    const shopParams = new URLSearchParams();
                    const autoCollect = queryParams.get('auto_collect');
                    const popupId = queryParams.get('popup_id');
                    if (autoCollect) shopParams.set('auto_collect', autoCollect);
                    if (popupId) shopParams.set('popup_id', popupId);
                    const shopQuery = shopParams.toString();
                    navigateToShopHard(`${ROUTES.SHOP}${shopQuery ? `?${shopQuery}` : ''}`);
                } else {
                    releaseLoginLoaderHandoff();
                    setLoginLoaderBgMode(false);
                    redirectBasedOnLoginResponse(result, router, failedLoginUrl);
                }

                return { success: result === LoginResult.SUCCESS, redirected: true };
            } catch (error) {
                reportLoginException(AuthMethods.PORTAL_REDIRECT, error);
                releaseLoginLoaderHandoff();
                setLoginLoaderBgMode(false);
                redirectBasedOnLoginResponse(LoginResult.FAILED, router);
                return { success: false, error: error instanceof Error ? error.message : 'Login failed' };
            }
        },

        handleJwtTokenLogin: async ({ sessionId, jwtToken, publisherId }: { sessionId: string, jwtToken: string, publisherId: string }): Promise<LoginActionResult> => {
            try {
                sessionStorage.setItem(JWT_SESSION_STORAGE_KEY, jwtToken);
                const { result, failedLoginUrl } = await performExternalLogin({
                    sessionId,
                    jwtToken,
                    publisherId,
                });
                if (result === LoginResult.SUCCESS) {
                    const checkoutToken = getCheckoutTokenFromUrl();
                    const d10xPayload = queryParams.get(URL_PARAMS.D10X_PAYLOAD);
                    if (d10xPayload) {
                        sessionStorage.setItem('dynamoPayload', d10xPayload);
                    }
                    // Do not merge location.search: acjwt on /shop is an auth
                    // callback → middleware 307s back to /login → Whoops.
                    navigateToShopHard(buildShopUrlForLoggedInCampaignCheckout(checkoutToken));
                    return { success: true, redirected: true };
                }
                const isFeatureOn = authBoot.featureFlags[FeatureFlags.STORE_CHECKOUT_LINK_BLOCK_SUCCESS_PAGE];
                const blockStoreParam = isFeatureOn ? '&shouldBlockStore=true' : '';
                const finalFailedUrl = failedLoginUrl ? `${failedLoginUrl}${blockStoreParam}` : `${ROUTES.FAILED_LOGIN}?error=auth${blockStoreParam}`;
                redirectBasedOnLoginResponse(result, router, finalFailedUrl);
                return { success: false, redirected: true };
            } catch (error) {
                reportLoginException(AuthMethods.TOKEN, error);
                const isFeatureOn = authBoot.featureFlags[FeatureFlags.STORE_CHECKOUT_LINK_BLOCK_SUCCESS_PAGE];
                const blockStoreParam = isFeatureOn ? '&shouldBlockStore=true' : '';
                redirectBasedOnLoginResponse(LoginResult.FAILED, router, `${ROUTES.FAILED_LOGIN}?error=auth${blockStoreParam}`);
                return { success: false, error: error instanceof Error ? error.message : 'Login failed' };
            }
        },

        // Restore the HttpOnly cookie before navigating to /shop. Edge + SSR
        // require the cookie; LS-only auto-login would otherwise bounce
        // login → shop → login forever (middleware never reaches the
        // boot/offers path that used to set session_expired).
        handleSessionDataLogin: async (): Promise<LoginActionResult> => {
            try {
                const sessionToken = sessionService.getSessionToken();
                if (!sessionToken) {
                    return { success: false, error: 'No session found' };
                }
                if (!(await setSessionCookieWithConfirmation(sessionToken))) {
                    return { success: false, error: 'Session cookie not stored' };
                }
                const campaignToken = getCampaignTokenFromUrl();
                if (campaignToken) {
                    const d10xPayload = queryParams.get(URL_PARAMS.D10X_PAYLOAD);
                    if (d10xPayload) {
                        sessionStorage.setItem('dynamoPayload', d10xPayload);
                    }
                    // Hard nav: soft /login→/shop paints LoginDocumentShell black
                    // while ShopClientWrapper loads — looks like a login loader.
                    navigateToShopHard(buildShopUrlForLoggedInCampaignCheckout(campaignToken));
                } else {
                    navigateToShopHard(ROUTES.SHOP);
                }
                return { success: true, redirected: true };
            } catch (error) {
                // Silent by design before this: no redirect, so the player is left
                // on the login screen holding a session that should have worked.
                reportLoginException(LoginStage.SESSION_DATA, error);
                return { success: false, error: error instanceof Error ? error.message : 'Session login failed' };
            }
        },

        handlePreLogin: async (): Promise<LoginActionResult> => {
            try {
                await sendPreLoginPublisherWebhookEvents();
                if (!isWebviewBrowser()) {
                    analytics.trackEvent(AnalyticsEventName.LOGIN_LAND, {
                        device_orientation: getDeviceOrientation(),
                    });
                }
                return { success: true };
            } catch (error) {
                // This is the call that tells the publisher the login screen was
                // shown. When it throws, the player is here but the publisher's
                // funnel says they never arrived.
                reportLoginException(LoginStage.PRE_LOGIN, error);
                return { success: false, error: error instanceof Error ? error.message : 'Pre-login failed' };
            }
        },

        handleFacebookLogin: async (): Promise<LoginActionResult> => {
            try {
                const facebookAccessToken = getHashParam(FACEBOOK_HASH);
                if (!facebookAccessToken) {
                    sendLoginCanceledWebhookEvents(AuthMethods.FACEBOOK);
                    return { success: false, error: 'No Facebook token found' };
                }
                const { result, failedLoginUrl } = await performLogin({
                    authMethod: AuthMethods.FACEBOOK,
                    localAddress,
                    userToken: facebookAccessToken
                }, authBoot);
                redirectBasedOnLoginResponse(result, router, failedLoginUrl);
                return { success: result === LoginResult.SUCCESS, redirected: true };
            } catch (error) {
                reportLoginException(AuthMethods.FACEBOOK, error);
                redirectBasedOnLoginResponse(LoginResult.FAILED, router);
                return { success: false, error: error instanceof Error ? error.message : 'Facebook login failed' };
            }
        },

        handleAppleLogin: async (): Promise<LoginActionResult> => {
            try {
                const appleCode = getHashParam(APPLE_HASH_TOKEN);
                if (!appleCode) {
                    sendLoginCanceledWebhookEvents(AuthMethods.APPLE);
                    return { success: false, error: 'No Apple code found' };
                }
                const { result, failedLoginUrl } = await performLogin({
                    authMethod: AuthMethods.APPLE,
                    localAddress,
                    userToken: appleCode
                }, authBoot);
                redirectBasedOnLoginResponse(result, router, failedLoginUrl);
                return { success: result === LoginResult.SUCCESS, redirected: true };
            } catch (error) {
                reportLoginException(AuthMethods.APPLE, error);
                redirectBasedOnLoginResponse(LoginResult.FAILED, router);
                return { success: false, error: error instanceof Error ? error.message : 'Apple login failed' };
            }
        },

        handleOTPLogin: async (proofKey?: string, token?: string): Promise<LoginActionResult> => {
            try {
                if (!proofKey) {
                    return { success: false, error: 'Missing OTP credentials' };
                }

                const utms = getUtmParams();
                if (token && Object.keys(utms).length === 0) {
                    const utmData = await getUtms(token).catch((error) => {
                        coralogixRumService.sendErrorEvent(LoggerEventNames.LOGIN_ERROR, { error, message: 'Failed to fetch UTMs from backend' });
                        return {};
                    });
                    if (Object.keys(utmData).length > 0) {
                        webStorageUtil.set(ESessionStorageKeys.UTM_PARAMS, utmData);
                    }
                }
                
                const { result, failedLoginUrl } = await performLogin({
                    authMethod: AuthMethods.OTP,
                    localAddress,
                    otp: {
                        playerCode: proofKey,
                        ...(token && { accessToken: token }),
                    }
                }, authBoot);

                if (result === LoginResult.SUCCESS) {
                    if (token) {
                        try {
                            const { getCampaignMetadata } = await import('@/lib/auth/otp');
                            const metadata = await getCampaignMetadata(token);
                            if (metadata.d10xPayload) {
                                sessionStorage.setItem('dynamoPayload', metadata.d10xPayload);
                            }
                            if (metadata.campaignToken) {
                                navigateToShopHard(buildShopUrlForLoggedInCampaignCheckout(metadata.campaignToken));
                                return { success: true, redirected: true };
                            }
                        } catch (error) {
                            console.error('Error getting campaign token', error);
                        }
                    }
                    redirectBasedOnLoginResponse(result, router, failedLoginUrl);
                    return { success: true, redirected: true };
                }

                // A concurrent OTP attempt may already have succeeded and set the
                // session; don't clobber that with /failed ("Whoops Login failed").
                const existingSession =
                    sessionService.getSessionToken() ??
                    webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN);
                if (existingSession) {
                    navigateToShopHard();
                    return { success: true, redirected: true };
                }

                redirectBasedOnLoginResponse(result, router, failedLoginUrl);
                return { success: false, redirected: true };
            } catch (error) {
                const existingSession =
                    sessionService.getSessionToken() ??
                    webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN);
                // Recovering into the shop is a hard navigation, which destroys
                // this document before RUM can send anything.
                reportLoginException(AuthMethods.OTP, error, {
                    crossesNavigation: Boolean(existingSession),
                });
                if (existingSession) {
                    navigateToShopHard();
                    return { success: true, redirected: true };
                }
                redirectBasedOnLoginResponse(LoginResult.FAILED, router);
                return { success: false, error: error instanceof Error ? error.message : 'OTP login failed' };
            }
        },

        handleUsernamePasswordLogin: async (username: string, password: string): Promise<LoginActionResult> => {
            try {
                if (!username?.trim() || !password?.trim()) {
                    return { success: false, error: 'Username and password are required' };
                }

                const { result, failedLoginUrl } = await performLogin({
                    authMethod: AuthMethods.USERNAME,
                    userName: username.trim(),
                    userPassword: password,
                    localAddress,
                }, authBoot);
                
                redirectBasedOnLoginResponse(result, router, failedLoginUrl);
                return { success: result === LoginResult.SUCCESS, redirected: true };
            } catch (error) {
                reportLoginException(AuthMethods.USERNAME, error);
                redirectBasedOnLoginResponse(LoginResult.FAILED, router);
                return { success: false, error: error instanceof Error ? error.message : 'Username/password login failed' };
            }
        },
    };
}

/**
 * A login that throws — network drop, aborted request, closed webview — never
 * reaches handleFailedLogin, so it produced no publisher event, no analytics and
 * no RUM record. The player lands on /failed (or nowhere) and the journey looks
 * like it never happened, which is what publishers report as a lost player.
 *
 * `crossesNavigation` is for the callers that hard-navigate straight after: the
 * soft redirects keep this document, and with it the queue RUM flushes on init.
 */
const reportLoginException = (
    authMethod: string,
    error: unknown,
    { crossesNavigation = false }: { crossesNavigation?: boolean } = {}
): void => {
    const payload = {
        authMethod,
        // Message only: an exception can carry a request URL with credentials in it.
        errorMessage: error instanceof Error ? error.message : String(error),
    };

    if (crossesNavigation) {
        coralogixRumService.sendEventAcrossNavigation(
            RumEventLevel.ERROR,
            LoggerEventNames.LOGIN_ERROR,
            payload
        );
        return;
    }
    coralogixRumService.sendErrorEvent(LoggerEventNames.LOGIN_ERROR, payload);
};